import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import CustomInput from '../../components/customInput/CustomInput';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';

import ModalFileUpload from '../../components/ModalFileUpload/ModalFileUpload';
import { useDispatch } from 'react-redux';
import { uploadAsset, deleteAsset } from '../../redux/assetSlice';
import { ASSET_LOCATION_MAPPING, ASSET_TYPE_MAPPING } from '../../utils/constant';
import { fetchIndustryContent } from '../../redux/homePageSlice';

const CustomerStories = forwardRef(({ language, onShowAlert = () => { } }, ref) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [stories, setStories] = useState([
    {
      logo: "",
      stories: "",
      name: "",
      position: "",
      company: "",
      borderColor: "",
      btnLabel: "",
      btnUrl: ""
    }
  ]);

  const [showDeleteStoryModal, setShowDeleteStoryModal] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);

  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);

  useEffect(() => {
    if (language) {
      dispatch(fetchIndustryContent({
        language,
        page: 'customerStories',
        region: 'SG',
        type: 'homePage'
      }))
        .then((result) => {
          if (result.payload) {
            const contentData = result.payload.resdata?.contentData;

            if (contentData) {
              setTitle(contentData.title || '');
              if (contentData.items && contentData.items.length > 0) {
                setStories(contentData.items);
              }
            }
          }
        })
        .catch((error) => {
          console.error('Failed to fetch customer stories:', error);
        });
    }
  }, [language, dispatch]);

  const PLACEHOLDER_CONFIG = {
    title: {
      EN: "Enter title in EN",
      CN: "Enter title in CN"
    },
    logo: {
      EN: "Enter logo path",
      CN: "Enter logo path"
    },
    stories: {
      EN: "Enter customer story in EN",
      CN: "Enter customer story in CN"
    },
    name: {
      EN: "Enter name",
      CN: "Enter name"
    },
    position: {
      EN: "Enter name",
      CN: "Enter name"
    },
    company: {
      EN: "Enter company",
      CN: "Enter company"
    },
    borderColor: {
      EN: "Enter border color",
      CN: "Enter border color"
    },
    buttonLabel: {
      EN: "Enter button label",
      CN: "Enter button label"
    },
    buttonUrl: {
      EN: "Enter button URL",
      CN: "Enter button URL"
    }
  };

  const addGroup = () => {
    setStories([
      ...stories,
      {
        logo: "",
        stories: "",
        name: "",
        position: "",
        company: "",
        borderColor: "",
        btnLabel: "",
        btnUrl: ""
      }
    ]);
  };

  const handleConfirmDeleteStory = () => {
    if (storyToDelete !== null) {
      const newItems = stories.filter((_, i) => i !== storyToDelete);
      setStories(newItems);
    }

    setShowDeleteStoryModal(false);
    setStoryToDelete(null);
  };

  const updateGroup = (index, field, value) => {
    const newItems = stories.map((story, i) =>
      i === index ? { ...story, [field]: value } : story
    );
    setStories(newItems);
  };

  const updateTitle = (value) => {
    setTitle(value);
  };

  const validateAllStories = () => {
    return {};
  };

  const handleShowUploadFile = (storyIndex, field) => {
    setSelectedStoryIndex(storyIndex);
    setSelectedField(field);
    setShowFileUploadModal(true);
  };

  const handleShowDeleteFile = (storyIndex, field) => {
    setSelectedStoryIndex(storyIndex);
    setSelectedField(field);
    setIsShowDeleteAsset(true);
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    setSelectedStoryIndex(null);
    setSelectedField(null);
    setIsUploading(false);
  };

  const handleFileUpload = (file) => {
    if (!file) return;

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("assetLocation", "customerStories");
    formData.append("assetType", ASSET_TYPE_MAPPING.homePage);

    dispatch(uploadAsset(formData))
      .then((res) => {
        if (res?.payload) {
          const fileUrl = res.payload.resdata.url;

          const newItems = stories.map((story, i) =>
            i === selectedStoryIndex ? { ...story, [selectedField]: fileUrl } : story
          );

          setStories(newItems);
        }
        if (res?.error) {
          onShowAlert(res.error.message, false);
        }
      })
      .catch((err) => {
        onShowAlert(err.message || "Upload failed", false);
      })
      .finally(() => {
        setIsUploading(false);
        setShowFileUploadModal(false);
        setSelectedStoryIndex(null);
        setSelectedField(null);
      });
  };

  const handleDeleteFile = () => {
    const fileUrl = stories[selectedStoryIndex]?.logo;

    const fileId = fileUrl ? fileUrl.split('/').pop().split('.')[0] : null;

    const newItems = stories.map((story, i) =>
      i === selectedStoryIndex ? { ...story, [selectedField]: "" } : story
    );

    setStories(newItems);

    if (fileId) {
      dispatch(deleteAsset(fileId))
        .then((res) => {
          if (res?.error) {
            onShowAlert(res.error.message, false);
          }
        })
        .catch((err) => {
          onShowAlert(err.message, false);
        });
    }

    setIsShowDeleteAsset(null);
    setSelectedStoryIndex(null);
    setSelectedField(null);
  };

  useImperativeHandle(ref, () => ({
    addGroup,
    validateAllStories,
    getData: () => ({
      title,
      items: stories
    })
  }));

  const handleShowDeleteStory = (index) => {
    if (stories.length <= 1) {
      alert('At least retain one customer story');
      return;
    }
    setStoryToDelete(index);
    setShowDeleteStoryModal(true);
  };

  const renderFileUploadSection = (story, index) => (
    <div className='package-input'>
      <label>Logo:</label>
      <input
        type="text"
        placeholder={PLACEHOLDER_CONFIG.logo[language]}
        value={story.logo || ''}
        disabled
        style={{ width: '73%' }}
      />
      {!story.logo ? (
        <button
          className="upload-btn"
          onClick={() => handleShowUploadFile(index, "logo")}
        >
          Upload
        </button>
      ) : (
        <button
          className="icon-btn"
          onClick={() => handleShowDeleteFile(index, "logo")}
        >
          <img src={DeleteIcon} alt="Delete" className="action-icon" />
        </button>
      )}
    </div>
  );

  return (
    <div className="tab-package-container">
      <div className="domain-group">
        <div className='package-input'>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <label>
              Title:
            </label>
          </div>
          <CustomInput
            type="text"
            value={title}
            onChange={(e) => updateTitle(e.target.value)}
            placeholder={PLACEHOLDER_CONFIG.title[language]}
            customStyle={{ width: '73%' }}
          />
        </div>
      </div>

      <div
        style={{
          height: '0.5px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          margin: '25px -40px',
          width: 'calc(100% + 80px)',
          display: 'block',
          boxSizing: 'border-box'
        }}
      />

      {stories.map((story, index) => (
        <div key={index}>
          <div className="domain-group">

            <div className='package-input'>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label>
                  Name:
                </label>
              </div>
              <CustomInput
                type="text"
                value={story.name || ''}
                onChange={(e) => updateGroup(index, 'name', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.name[language]}
                customStyle={{ width: '73%' }}
              />
              <button
                className="icon-btn"
                onClick={() => handleShowDeleteStory(index)}
              >
                <img src={DeleteIcon} alt="Delete" className="campaign-delete-icon" />
              </button>
            </div>

            {renderFileUploadSection(story, index)}
            {story.logo && (
              <div style={{ marginLeft: '190px', marginBottom: '20px' }}>
                <img
                  src={`https://uat.stack-ez.com${story.logo}`}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }}
                />
              </div>
            )}

            <div className='package-input'>
              <label>Stories:</label>
              <textarea
                placeholder={PLACEHOLDER_CONFIG.stories[language]}
                value={story.stories || ''}
                onChange={(e) => updateGroup(index, 'stories', e.target.value)}
                rows={4}
              />
            </div>

            <div className='package-input'>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label>
                  Position:
                </label>
              </div>
              <CustomInput
                type="text"
                value={story.position || ''}
                onChange={(e) => updateGroup(index, 'position', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.position[language]}
                customStyle={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label>
                  Company:
                </label>
              </div>
              <CustomInput
                type="text"
                value={story.company || ''}
                onChange={(e) => updateGroup(index, 'company', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.company[language]}
                customStyle={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label>
                  Border Color:
                </label>
              </div>
              <CustomInput
                type="text"
                value={story.borderColor || ''}
                onChange={(e) => updateGroup(index, 'borderColor', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.borderColor[language]}
                customStyle={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label>
                  Button Label:
                </label>
              </div>
              <CustomInput
                type="text"
                value={story.btnLabel || ''}
                onChange={(e) => updateGroup(index, 'btnLabel', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.buttonLabel[language]}
                customStyle={{ width: '73%' }}
              />
            </div>

            <div className='package-input'>
              <label>Button Url:</label>
              <CustomInput
                type="text"
                value={story.btnUrl || ''}
                onChange={(e) => updateGroup(index, 'btnUrl', e.target.value)}
                placeholder={PLACEHOLDER_CONFIG.buttonUrl[language]}
                customStyle={{ width: '73%' }}
              />
            </div>
          </div>

          {index !== stories.length - 1 && (
            <div
              style={{
                height: '0.5px',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                margin: '25px -40px',
                width: 'calc(100% + 80px)',
                display: 'block',
                boxSizing: 'border-box'
              }}
            />
          )}
        </div>
      ))}

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={handleFileUpload}
        isLoading={isUploading}
      />

      <CommonDeleteModal
        show={!!isShowDeleteAsset}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
        title="Delete Asset"
        instruction="Are you sure you want to delete this asset?"
      />

      <CommonDeleteModal
        show={showDeleteStoryModal}
        onHide={() => {
          setShowDeleteStoryModal(false);
          setStoryToDelete(null);
        }}
        onConfirm={handleConfirmDeleteStory}
        title="Delete Customer Story"
        instruction="Are you sure you want to delete this customer story item?"
      />
    </div>
  );
});

export default CustomerStories;